<template>
  <div>
    <v-dialog
      scrollable
      width="750px"
      :value="show"
      :persistent="isSyncingData"
      @click:outside="handleClose"
      @keydown.esc="handleClose"
    >
      <v-card>
        <v-card-title class="px-8 pt-6"
          >{{ $t('problem_solving.gaps_analysis_tool.title') }}
          <p class="mb-0 pb-0 px-0 mt-4 body3 grey--text text--darken-1">
            {{ $t('problem_solving.gaps_analysis_tool.description') }}
          </p></v-card-title
        >
        <v-divider />
        <v-card-text class="content" data-tourid="gaps-tool-content">
          <GapForm
            data-tourid="gaps-tool-form"
            class="mt-4"
            v-if="canAddGaps"
            :gap="newGap"
            :isLoading="isSyncingData"
          />

          <div class="d-flex justify-end" v-if="canAddGaps" cols="12">
            <Button
              color="primary"
              data-tourid="gaps-tool-form-submit"
              :disabled="!canAddGap || isUpdatingGaps"
              :loading="isAddingGap"
              :text="$t('problem_solving.gaps_analysis_tool.add_gap_button')"
              @click="addGap"
            />
          </div>

          <v-card-title
            v-if="gaps.length > 0"
            class="mt-6 px-0 py-0 black--text"
            >{{
              $t('problem_solving.gaps_analysis_tool.added_gaps_title', {
                totalGaps: gaps.length,
              })
            }}</v-card-title
          >

          <GapCard
            v-for="(gap, index) in gaps"
            class="mt-2"
            data-tourid="gaps-tool-card"
            :editable="!isUpdatingGaps && canAddGaps"
            :key="index"
            :index="index"
            :gap="gap"
            :isLoading="isUpdatingGaps && currentGapIndex === index"
            @save="updateGap($event, index)"
            @remove="handleRemove(index)"
          />
          <v-card outlined color="blue lighten-5 mt-7">
            <v-card-text class="text-center blue--text">{{
              $t('problem_solving.gaps_analysis_tool.gaps_value_banner')
            }}</v-card-text>
          </v-card>
        </v-card-text>

        <v-divider class="ma-0" />
        <v-card-actions class="justify-end px-8 py-6">
          <Button
            plain
            color="grey"
            data-tourid="gaps-tool-close"
            :disabled="isSyncingData"
            :text="$t('common.button.close')"
            @click="handleClose"
          />
        </v-card-actions>
      </v-card>
    </v-dialog>
    <DeleteConfirmationModal
      :show="showDeleteGapModal"
      :isLoading="isDeletingGap"
      :title="$t('problem_solving.gaps_analysis_tool.delete_gap_title')"
      :text="$t('problem_solving.gaps_analysis_tool.delete_gap_message')"
      @cancel="showDeleteGapModal = false"
      @delete="deleteGap"
    />
  </div>
</template>

<script>
import { mapActions } from 'vuex'

import mixin from '@/mixins/mixin.vue'
import GapForm from '../GapForm.vue'
import GapCard from '../GapCard.vue'
import DeleteConfirmationModal from '@/components/Modals/DeleteConfirmationModal.vue'
import Button from '@/components/UI/Button'

export default {
  name: 'GapsIdentificationTool',

  mixins: [mixin],

  components: {
    GapForm,
    GapCard,
    DeleteConfirmationModal,
    Button,
  },

  props: {
    canAddGaps: {
      type: Boolean,
      required: true,
    },
    show: {
      type: Boolean,
      required: true,
    },
    rev: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      newGap: {},
      currentGapIndex: null,
      isAddingGap: false,
      isUpdatingGaps: false,
      isDeletingGap: false,
      showDeleteGapModal: false,
    }
  },

  computed: {
    gaps() {
      return this.rev?.processGaps ?? []
    },

    canAddGap() {
      return (
        this.newGap.expectation?.trim?.() &&
        this.newGap.reality?.trim?.() &&
        this.newGap.conclusion?.trim?.()
      )
    },

    isSyncingData() {
      return this.isAddingGap || this.isUpdatingGaps
    },
  },

  methods: {
    ...mapActions({
      get: 'problemSolving/get',
      update: 'problemSolving/update',
    }),

    handleClose() {
      if (this.isSyncingData) return
      this.$emit('close')
    },

    async addGap() {
      try {
        this.isAddingGap = true

        await this.updateRev({
          processGaps: [...this.gaps, this.newGap],
        })

        this.newGap = {}
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        this.isAddingGap = false
      }
    },

    async updateGap(gap, index) {
      try {
        this.isUpdatingGaps = true
        this.currentGapIndex = index

        const newGaps = [...this.gaps]
        newGaps.splice(index, 1, gap)

        await this.updateRev({ processGaps: newGaps })
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        this.isUpdatingGaps = false
        this.currentGapIndex = null
      }
    },

    handleRemove(index) {
      this.currentGapIndex = index
      this.showDeleteGapModal = true
    },

    async deleteGap(index) {
      try {
        this.isDeletingGap = true

        const newGaps = [...this.gaps]
        newGaps.splice(index, 1)

        await this.updateRev({ processGaps: newGaps })
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        this.isDeletingGap = false
        this.currentGapIndex = null
        this.showDeleteGapModal = false
      }
    },

    async updateRev(data) {
      await this.update({
        organizationId: this.rev.organization,
        revId: this.rev.id,
        data: { ...this.rev, ...data },
      })

      await this.get({
        organizationId: this.rev.organization,
        revId: this.rev.id,
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.content {
  max-height: 600px;
}
</style>
